import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SendLiveButton, useNotificationsStatus } from "@/lib/notifications";
import {
  Mail, MessageSquare, Copy, Check, ExternalLink,
  History, ShieldCheck, Loader2, ChevronDown, ChevronUp, AlertTriangle,
  Settings as SettingsIcon, FileWarning, CheckCircle2, Clock,
} from "lucide-react";

function NotificationsTip() {
  const status = useNotificationsStatus();
  if (!status) return null;
  const e = status.email?.configured;
  const s = status.sms?.configured;
  if (e && s) return <span>Live email + SMS auto-send is on — tap “Send now” to fire.</span>;
  if (e && !s) return <span>Email auto-send is live; SMS will go live once your Twilio bundle clears.</span>;
  if (!e && s) return <span>SMS auto-send is live; email pending SMTP setup.</span>;
  return <span>Tip: auto-send by email + SMS lights up once SMTP and Twilio keys are added.</span>;
}

function CopyButton({ text, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Couldn't copy — please select & copy manually");
    }
  };
  return (
    <button onClick={onCopy} className="btn-outline text-xs" data-testid={testid}>
      {done ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

export default function CheckInPanel({ job, onOpenSettings, onCheckedIn }) {
  const [tab, setTab] = useState("sms"); // "sms" | "email"
  const [msg, setMsg] = useState(null);
  const [history, setHistory] = useState(null);
  const [mot, setMot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  // Live-editable promised ready time — set from diary on import, but the
  // advisor often needs to nudge it at check-in if the customer changed
  // their mind ("actually, can I have it by 3 instead of 5?").
  const [promised, setPromised] = useState(job?.promised_ready_time || "");
  const [savingPromised, setSavingPromised] = useState(false);
  useEffect(() => { setPromised(job?.promised_ready_time || ""); }, [job?.promised_ready_time]);

  useEffect(() => {
    if (!job?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/jobs/${job.id}/welcome-message`).then(r => r.data).catch(() => null),
      job.vehicle_reg
        ? api.get(`/vehicles/${encodeURIComponent(job.vehicle_reg)}/history`).then(r => r.data).catch(() => null)
        : Promise.resolve(null),
    ]).then(([m, h]) => {
      setMsg(m);
      setHistory(h);
      setLoading(false);
    });
  }, [job?.id, job?.vehicle_reg]);

  const lookupMot = async () => {
    if (!job?.vehicle_reg) { toast.error("Add a registration first"); return; }
    setMot({ loading: true });
    try {
      const r = await api.get(`/vehicles/${encodeURIComponent(job.vehicle_reg)}/mot`);
      setMot(r.data);
    } catch (e) {
      setMot({ error: e?.response?.data?.detail || "Lookup failed" });
    }
  };

  // Mark the customer as physically arrived. Surfaces a clear ticket
  // straight on the job page so service advisors don't have to bounce
  // back to the dashboard — and the journey timeline updates instantly.
  const savePromised = async (val) => {
    const clean = (val || "").trim();
    if (clean === (job?.promised_ready_time || "").trim()) return;
    setSavingPromised(true);
    try {
      await api.patch(`/jobs/${job.id}`, { promised_ready_time: clean || null });
      toast.success(clean ? `Promised ready time updated to ${clean}` : "Promised ready time cleared");
      onCheckedIn?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save promised ready time");
      setPromised(job?.promised_ready_time || "");
    } finally {
      setSavingPromised(false);
    }
  };

  const handleCheckIn = async () => {
    if (!job?.id || checkingIn) return;
    setCheckingIn(true);
    try {
      const r = await api.post(`/jobs/${job.id}/check-in`, { delay_minutes: 5 });
      if (r?.data?.ok) {
        toast.success("Customer checked in — welcome message will fire in 5 minutes");
        onCheckedIn?.({
          checked_in_at: r.data.checked_in_at,
          welcome_scheduled_at: r.data.welcome_scheduled_at,
          welcome_dispatched_at: null,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed — please try again");
    } finally {
      setCheckingIn(false);
    }
  };

  const handleUndoCheckIn = async () => {
    if (!job?.id || checkingIn) return;
    setCheckingIn(true);
    try {
      const r = await api.post(`/jobs/${job.id}/cancel-check-in`);
      if (r?.data?.ok) {
        toast.message("Check-in cancelled");
        onCheckedIn?.({
          checked_in_at: null,
          welcome_scheduled_at: null,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel check-in");
    } finally {
      setCheckingIn(false);
    }
  };

  const checkedInAt = job?.checked_in_at
    ? new Date(job.checked_in_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  if (loading) {
    return (
      <div className="card-default p-5 flex items-center gap-3 text-sm text-muted-foreground" data-testid="checkin-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Preparing check-in…
      </div>
    );
  }

  const smsHref = msg?.to_phone
    ? `sms:${encodeURIComponent(msg.to_phone)}?&body=${encodeURIComponent(msg.sms_body || "")}`
    : null;
  const mailHref = msg?.to_email
    ? `mailto:${encodeURIComponent(msg.to_email)}?subject=${encodeURIComponent(msg.email_subject || "")}&body=${encodeURIComponent(msg.email_body || "")}`
    : null;

  return (
    <div className="card-default" data-testid="checkin-panel">
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left"
        data-testid="checkin-toggle"
      >
        <div>
          <div className="label-overline">Check-in</div>
          <div className="font-display font-bold text-xl mt-1">Welcome message &amp; vehicle intel</div>
        </div>
        <div className="flex items-center gap-2">
          {checkedInAt ? (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/15 text-success border border-success/30 text-[11px] font-bold uppercase tracking-wider"
              data-testid="checkin-status-pill"
              title={`Checked in at ${checkedInAt}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Checked in · {checkedInAt}
            </span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              aria-disabled={checkingIn}
              onClick={(e) => { e.stopPropagation(); if (!checkingIn) handleCheckIn(); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !checkingIn) { e.stopPropagation(); handleCheckIn(); } }}
              className={`btn-primary text-xs ${checkingIn ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="checkin-mark-now-btn"
              title="Mark this customer as arrived — welcome message will fire in 5 minutes"
            >
              {checkingIn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {checkingIn ? "Checking in…" : "Mark checked in"}
            </span>
          )}
          {onOpenSettings && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onOpenSettings(); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onOpenSettings(); } }}
              className="btn-outline text-xs"
              data-testid="checkin-edit-template-btn"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> Edit template
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5">
          {/* Editable promised-ready time — set from diary OUT time on import.
              The advisor often needs to nudge this at check-in when the
              customer changes their mind ("actually, can I have it by 3?"). */}
          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 flex items-center gap-3 flex-wrap" data-testid="checkin-promised-ready-row">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] label-overline text-primary">Promised ready time</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Pulled from the diary&rsquo;s OUT time — adjust here if the customer&rsquo;s plans have changed.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="time"
                value={promised}
                onChange={(e) => setPromised(e.target.value)}
                onBlur={() => savePromised(promised)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePromised(promised); e.currentTarget.blur(); } }}
                disabled={savingPromised}
                className="input-base h-9 w-28 text-center font-mono-plex font-bold text-sm"
                data-testid="checkin-promised-ready-input"
              />
              {savingPromised && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              {!savingPromised && promised && (
                <button
                  type="button"
                  onClick={() => { setPromised(""); savePromised(""); }}
                  className="btn-outline text-xs"
                  data-testid="checkin-promised-ready-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Live check-in status banner — replaces the silent dashboard-only flow */}
          {checkedInAt ? (
            <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 px-3 py-2.5" data-testid="checkin-confirmation-banner">
              <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold text-success">Checked in at {checkedInAt}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {job?.welcome_dispatched_at
                    ? "Welcome message dispatched ✓"
                    : job?.welcome_scheduled_at
                      ? <>Welcome will auto-fire at <span className="font-mono-plex">{new Date(job.welcome_scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span></>
                      : "Welcome scheduled"}
                </div>
              </div>
              {!job?.welcome_dispatched_at && (
                <button
                  type="button"
                  onClick={handleUndoCheckIn}
                  disabled={checkingIn}
                  className="btn-outline text-xs shrink-0"
                  data-testid="checkin-undo-btn"
                >
                  Undo
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5" data-testid="checkin-prompt-banner">
              <Clock className="w-4 h-4 text-warning mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold">Customer hasn&apos;t been checked in yet</div>
                <div className="text-xs text-muted-foreground mt-0.5">Tap &ldquo;Mark checked in&rdquo; above as soon as they arrive — the welcome SMS &amp; email auto-fire 5 minutes later.</div>
              </div>
              <button
                type="button"
                onClick={handleCheckIn}
                disabled={checkingIn}
                className="btn-primary text-xs shrink-0"
                data-testid="checkin-prompt-btn"
              >
                {checkingIn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {checkingIn ? "…" : "Check in"}
              </button>
            </div>
          )}

          {/* Welcome message */}
          <div>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/40 border border-border w-fit">
              <button
                onClick={() => setTab("sms")}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${tab === "sms" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="checkin-tab-sms"
              >
                <MessageSquare className="w-3.5 h-3.5" /> SMS
              </button>
              <button
                onClick={() => setTab("email")}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${tab === "email" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="checkin-tab-email"
              >
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
            </div>

            {tab === "sms" ? (
              <div className="mt-3 space-y-3" data-testid="checkin-sms-pane">
                <div className="rounded-lg bg-muted/30 border border-border p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="checkin-sms-body">
                  {msg?.sms_body}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CopyButton text={msg?.sms_body} label="Copy SMS" testid="checkin-copy-sms" />
                  {smsHref ? (
                    <a href={smsHref} className="btn-outline text-xs" data-testid="checkin-open-sms">
                      <MessageSquare className="w-3.5 h-3.5" /> Open in Messages
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <FileWarning className="w-3.5 h-3.5" /> No phone on file
                    </span>
                  )}
                  <SendLiveButton
                    endpoint={`/jobs/${job.id}/send-welcome`}
                    channel="sms"
                    disabled={!msg?.to_phone}
                    testid="checkin-send-sms-live"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-3" data-testid="checkin-email-pane">
                <div className="rounded-lg bg-muted/30 border border-border overflow-hidden">
                  <div className="px-3 py-2 border-b border-border text-xs font-semibold" data-testid="checkin-email-subject">
                    {msg?.email_subject}
                  </div>
                  <div className="p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="checkin-email-body">
                    {msg?.email_body}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CopyButton text={msg?.email_body} label="Copy body" testid="checkin-copy-email-body" />
                  <CopyButton text={msg?.email_subject} label="Copy subject" testid="checkin-copy-email-subject" />
                  {mailHref ? (
                    <a href={mailHref} className="btn-outline text-xs" data-testid="checkin-open-email">
                      <Mail className="w-3.5 h-3.5" /> Open in Mail
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <FileWarning className="w-3.5 h-3.5" /> No email on file
                    </span>
                  )}
                  <SendLiveButton
                    endpoint={`/jobs/${job.id}/send-welcome`}
                    channel="email"
                    disabled={!msg?.to_email}
                    testid="checkin-send-email-live"
                  />
                </div>
              </div>
            )}

            <div className="mt-2 text-xs text-muted-foreground" data-testid="checkin-tip">
              <NotificationsTip />
            </div>
          </div>

          <hr className="border-border" />

          {/* Vehicle intel */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Service history */}
            <div className="rounded-lg border border-border p-4" data-testid="checkin-history">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-primary" />
                <div className="label-overline">Service history</div>
              </div>
              {history?.count > 0 ? (
                <div className="mt-3 space-y-2">
                  <div className="text-sm">
                    <span className="font-display font-bold text-2xl tracking-tight">{history.count}</span>
                    <span className="text-muted-foreground ml-1.5">
                      prior visit{history.count === 1 ? "" : "s"} for <span className="font-mono-plex text-foreground">{history.reg}</span>
                    </span>
                  </div>
                  <ul className="text-xs space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {history.jobs.map((j) => (
                      <li key={j.id} className="flex items-center justify-between gap-2 py-1 border-b border-border/60 last:border-0">
                        <div className="min-w-0">
                          <div className="font-mono-plex text-[10px] text-muted-foreground">
                            {j.created_at ? new Date(j.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                          </div>
                          <div className="truncate">{j.customer_name || "—"}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">{j.status}</div>
                          <div className="font-mono-plex">£{(j.approved_total || 0).toFixed(0)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-3 text-sm text-muted-foreground">
                  First time we've seen <span className="font-mono-plex text-foreground">{(job?.vehicle_reg || "this plate").toUpperCase()}</span>.
                </div>
              )}
            </div>

            {/* MOT */}
            <div className="rounded-lg border border-border p-4" data-testid="checkin-mot">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <div className="label-overline">DVSA MOT status</div>
              </div>
              {!mot ? (
                <div className="mt-3 space-y-3">
                  <div className="text-sm text-muted-foreground">Pull the latest MOT history straight from DVSA.</div>
                  <button onClick={lookupMot} className="btn-outline text-xs" data-testid="checkin-mot-lookup-btn">
                    <ShieldCheck className="w-3.5 h-3.5" /> Check MOT
                  </button>
                </div>
              ) : mot.loading ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Looking up DVSA…
                </div>
              ) : mot.error ? (
                <div className="mt-3 text-sm text-destructive flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {mot.error}
                </div>
              ) : mot.configured === false ? (
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-start gap-2 text-warning">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Free DVSA Trade API access not yet set up.</span>
                  </div>
                  <a
                    href="https://documentation.history.mot.api.gov.uk/"
                    target="_blank" rel="noreferrer"
                    className="btn-primary text-xs w-fit"
                    data-testid="checkin-mot-apply-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Apply for free DVSA key
                  </a>
                  <p className="text-xs text-muted-foreground">Usually approved in 24–48h. Paste the credentials into the backend .env and the lookup goes live.</p>
                </div>
              ) : !mot.found ? (
                <div className="mt-3 text-sm text-muted-foreground">No MOT history found at DVSA for {mot.reg}.</div>
              ) : (
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="font-display font-bold text-base">{[mot.make, mot.model].filter(Boolean).join(" ")}</div>
                      <div className="text-xs text-muted-foreground">{mot.fuel_type} · {mot.primary_colour}</div>
                    </div>
                    {mot.mot_expiry && (
                      <div className="text-right">
                        <div className="label-overline">Expires</div>
                        <div className="font-mono-plex text-sm">{new Date(mot.mot_expiry).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                      </div>
                    )}
                  </div>
                  {mot.latest_test && (
                    <div className="rounded-md bg-muted/40 p-2.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-bold uppercase tracking-wider">Latest test</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${mot.latest_test.result === "PASSED" ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>
                          {mot.latest_test.result || "—"}
                        </span>
                      </div>
                      <div className="text-muted-foreground mt-1">
                        {mot.latest_test.completed_at ? new Date(mot.latest_test.completed_at).toLocaleDateString("en-GB") : "—"}
                        {mot.latest_test.odometer_value ? ` · ${mot.latest_test.odometer_value} ${mot.latest_test.odometer_unit || ""}` : ""}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
